/* eslint-disable no-param-reassign */
import { defineMessages } from 'gatsby-plugin-react-intl';

import { formatNumberRange } from './formatting';
import { isArchived } from './courseState';
import { withI18nPrefix } from './i18n';

const SELF_PACED = 'self_paced';
const CURRENCY = 'USD';
const PAID_MODES = new Set(['verified', 'professional']);

const messages = defineMessages({
  'prospectus.course.effort_range': {
    id: 'prospectus.course.effort_range',
    description: 'Estimated range of effort to complete this course',
    defaultMessage: '{effortRange} hours',
  },

  'prospectus.course.course_count': {
    id: 'prospectus.course.course_count',
    description: 'Append course or courses to the number of courses',
    defaultMessage: '{courseCount, plural, '
      + 'one {# course} '
      + 'other {# courses}}',
  },
});

const sharedMessages = defineMessages({
  showMore: {
    id: 'prospectus.course.detail.main.show.more',
    description: 'Text for button to expand hidden selection',
    defaultMessage: 'Show more',
  },
  showLess: {
    id: 'prospectus.course.detail.main.show.less',
    description: 'Text for button to collapse expanded selection',
    defaultMessage: 'Show less',
  },
});

const mobilePageHeaders = defineMessages({
  'prospectus.course.detail.mobile.main.about': {
    id: 'prospectus.course.detail.mobile.main.about',
    description: 'Title for about this course',
    defaultMessage: 'About this course',
  },
  'prospectus.course.detail.mobile.main.outcomes': {
    id: 'prospectus.course.detail.mobile.main.outcomes',
    description: 'Title for course outcomes',
    defaultMessage: 'What you\'ll learn',
  },
  'prospectus.course.detail.mobile.main.instructors': {
    id: 'prospectus.course.detail.mobile.main.instructors',
    description: 'Title for instructors',
    defaultMessage: 'Instructors',
  },
  'prospectus.course.detail.mobile.main.faq': {
    id: 'prospectus.course.detail.mobile.main.faq',
    description: 'Title for FAQ ',
    defaultMessage: 'Frequently Asked Questions',
  },
  'prospectus.course.detail.mobile.main.course-modes': {
    id: 'prospectus.course.detail.mobile.main.course-modes',
    description: 'Title for course tracks',
    defaultMessage: 'Ways to take this course',
  },
  'prospectus.course.detail.mobile.main.share': {
    id: 'prospectus.course.detail.mobile.main.share',
    description: 'Title for social media share buttons',
    defaultMessage: 'Share this course',
  },
  'prospectus.course.detail.mobile.main.recommendations': {
    id: 'prospectus.course.detail.mobile.main.recommendations',
    description: 'Title for course recommendations',
    defaultMessage: 'Similar Courses',
  },
  'prospectus.course.detail.mobile.main.business': {
    id: 'prospectus.course.detail.mobile.main.business',
    description: 'Title for section that provides information on courses for businesses',
    defaultMessage: 'edX For Business',
  },
});

const getAggregateRating = (courseReview) => ({
  '@type': 'AggregateRating',
  ratingValue: Number(courseReview.avgCourseRating),
  ratingCount: courseReview.reviewsCount,
});

const getCourseCountString = (courseCount, intl) => (
  intl.formatMessage(
    messages['prospectus.course.course_count'],
    { courseCount },
  )
);

const getCoursePriceUSD = (courseRun) => {
  let price = null;

  if (courseRun && courseRun.seats) {
    courseRun.seats.forEach((seat) => {
      if (PAID_MODES.has(seat.type) && seat.currency === CURRENCY) {
        ({ price } = seat);
      }
    });
  }
  return price;
};

const getHoursOfEffort = (activeCourseRun, intl) => {
  const {
    minEffort,
    maxEffort,
  } = activeCourseRun;

  if (minEffort || maxEffort) {
    return intl.formatMessage(
      messages['prospectus.course.effort_range'],
      {
        effortRange: formatNumberRange(minEffort, maxEffort, intl),
      },
    );
  }

  return '';
};

// Returns first seat that can be used for business bulk purchase for the active course run
const getBusinessSeat = (courseRun) => {
  const { seats = [] } = courseRun;

  return seats.find(({ type }) => type === 'verified' || type === 'professional');
};

const hasStarted = (start) => {
  const today = new Date();
  const startDate = new Date(start);

  return startDate && today >= startDate;
};

const getCurrentRun = (search, course) => {
  let currentRun;
  if (search && search.run) {
    currentRun = course.activeCourseRuns.find(run => run.uuid === search.run);
  }
  return currentRun || course.activeCourseRun;
};

const courseIsPartOfMicroMasters = (course) => {
  const { programs = [] } = course;

  if (programs.length > 0) {
    const typeList = programs.map(({ type }) => type.toLowerCase());

    return typeList.indexOf('micromasters') > -1;
  }

  return false;
};

const getFullMarketingUrl = ({ prospectusPath }, locale = 'en') => {
  const i18nPath = withI18nPrefix(prospectusPath, locale);
  const marketingDomain = process.env.GATSBY_MARKETING_DOMAIN;
  return `${marketingDomain}${i18nPath}`;
};

const FREE = 'free';
const UPGRADABLE = 'upgradable';
const PAID = 'paid';

const calculatePaymentOption = seatTypes => {
  if (seatTypes.has('audit') && seatTypes.size === 1) {
    return FREE;
  } if (seatTypes.size > 0 && seatTypes.has('audit')) {
    return UPGRADABLE;
  } if (seatTypes.size > 0) {
    return PAID;
  }
  return null;
};

const getCoursePaymentOption = course => {
  const courseRuns = course.activeCourseRuns;
  const seatTypes = new Set();

  courseRuns.forEach(courseRun => {
    if (courseRun.seats) {
      courseRun.seats.forEach(seat => {
        seatTypes.add(seat.type);
      });
    }
  });

  return calculatePaymentOption(seatTypes);
};

const getCourseRunPaymentOption = courseRun => {
  const seatTypes = new Set();
  courseRun.seats.forEach(seat => {
    seatTypes.add(seat.type);
  });
  return calculatePaymentOption(seatTypes);
};

const getBusinessSeatUrl = activeCourseRun => {
  const businessSeat = getBusinessSeat(activeCourseRun);
  return businessSeat ? `https://ecommerce.edx.org/basket/add/?sku=${businessSeat.bulkSku}` : '';
};

const getRegularSeatUrl = activeCourseRun => {
  const regularSeat = getBusinessSeat(activeCourseRun);
  return regularSeat ? `https://ecommerce.edx.org/basket/add/?sku=${regularSeat.sku}` : '';
};

const getCourseImageUrl = course => (course?.image?.src || '');

const processCourse = (course, courseRun, intl) => {
  course.isSelfPaced = courseRun.pacingType === 'self_paced';
  course.isFreeOption = courseRun.type !== 'professional';

  // Use the first subject as the primary subject
  [course.primarySubject] = course.subjects;

  course.hoursOfEffort = getHoursOfEffort(courseRun, intl);
  course.weeksToComplete = courseRun.weeksToComplete;
  course.coursePrice = getCoursePriceUSD(courseRun);
};

// SERP HELPERS

const generateBaseOffer = (category) => ({ '@type': 'Offer', category });

const generatePaidOffer = (course) => {
  const offer = generateBaseOffer('Paid');
  offer.price = course.coursePrice;
  offer.priceCurrency = 'USD';
  return offer;
};

const getOffers = (course) => ([generateBaseOffer('Partially Free'), generatePaidOffer(course)]);

// Not quite sure that offers is correct on credential awarded.
const getEducationalCredentialAwarded = (course) => ({
  '@type': 'EducationalOccupationalCredential',
  name: 'edX Certificate',
  url: 'https://www.edx.org/verified-certificate',
  credentialCategory: 'Certificate',
  offers: [{
    '@type': 'Offer',
    priceCurrency: 'USD',
    price: course.coursePrice,
  }],
});

const getDuration = (activeCourseRun) => {
  const {
    minEffort,
    maxEffort,
  } = activeCourseRun;

  const processedMinEffort = Number.isNaN(minEffort) ? 0 : Number(minEffort);
  const processedMaxEffort = Number.isNaN(maxEffort) ? 0 : Number(maxEffort);
  const effortAverage = Math.floor((processedMinEffort + processedMaxEffort) / 2);

  return `PT${effortAverage}H`;
};

const getMappedCourseInstances = (course, instructorMap) => {
  const today = new Date();
  const mappedCourseInstances = course.activeCourseRuns.map(courseInstance => ({
    '@type': 'CourseInstance',
    instructor: instructorMap,
    name: course.title,
    courseMode: 'Online',
    courseSchedule: {
      '@type': 'Schedule',
      duration: getDuration(courseInstance),
      repeatFrequency: 'Weekly',
      repeatCount: courseInstance.weeksToComplete,
      startDate: courseInstance.pacingType === SELF_PACED
      && new Date(courseInstance.start).getTime() < today.getTime() ? today.toISOString() : courseInstance.start,
      endDate: courseInstance.end,
    },
  }));

  return mappedCourseInstances;
};

const getEducationalLevel = levelType => {
  const levelMap = {
    Introductory: 'Beginner',
    Intermediate: 'Intermediate',
    Advanced: 'Advanced',
  };

  return levelMap[levelType];
};

const getShortenedLanguageCode = languageCode => {
  if (languageCode?.length > 2) {
    return languageCode.slice(0, 2);
  }
  return languageCode;
};

const getTruncatedDescription = description => {
  // max length is 500 characters
  if (description?.length > 500) {
    return `${description.slice(0, 497)}...`;
  }
  return description;
};

export {
  courseIsPartOfMicroMasters,
  getAggregateRating,
  getBusinessSeat,
  hasStarted,
  getCourseCountString,
  isArchived,
  getFullMarketingUrl,
  getCoursePaymentOption,
  getCourseRunPaymentOption,
  FREE,
  UPGRADABLE,
  PAID,
  getBusinessSeatUrl,
  getRegularSeatUrl,
  getCourseImageUrl,
  sharedMessages,
  processCourse,
  getCurrentRun,
  getHoursOfEffort,
  getCoursePriceUSD,
  mobilePageHeaders,
  getOffers,
  getEducationalCredentialAwarded,
  getDuration,
  getMappedCourseInstances,
  getEducationalLevel,
  getShortenedLanguageCode,
  getTruncatedDescription,
};
